import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import { IoMdPrint } from "react-icons/io";
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import html2pdf from "html2pdf.js";
import logo from '../Photos/logo.png';
import '../css/others/printdoc.css';
import { Dimcontext } from '../../context/pagecontext';
import { Datacontext } from "../../context/dataContext"

// --- HELPER COMPONENTS (Identical to Professional Format) ---

const StatusBadge = ({ label, variant }) => {
    if (!label) return null;
    const v = (variant || label).toLowerCase();
    
    const badgeStyles = {
        padding: '5px 10px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    };

    let bgColor = '#e9ecef';
    let textColor = '#495057';

    if (['completed', 'yes', 'received'].includes(v)) {
        bgColor = '#d1e7dd';
        textColor = '#0f5132';
    } else if (v === 'approved') {
        bgColor = '#cff4fc';
        textColor = '#055160';
    } else if (v === 'checked') {
        bgColor = '#fff3cd';
        textColor = '#664d03';
    } else if (['no', 'rejected'].includes(v)) {
        bgColor = '#f8d7da';
        textColor = '#842029';
    } else if (v === 'pending') {
        bgColor = '#fff3cd';
        textColor = '#664d03';
    }

    return (
        <span className="pdf-badge" style={{ ...badgeStyles, backgroundColor: bgColor, color: textColor }}>
            {label}
        </span>
    );
};

const PdfBrandHeader = () => (
    <header className="pdf-brand-header page-break-avoid" style={{ borderBottom: '2px solid #f1f0f9', marginBottom: '20px', paddingBottom: '10px' }}>
        <div className='d-flex justify-content-center'>
            <img 
                src={logo} 
                alt='logo' 
                style={{ 
                    width: "70mm", 
                    maxWidth: "100%",    /* Allows shrinking on narrow mobile screens */
                    height: "auto",      /* Maintains aspect ratio when shrinking */
                    maxHeight: "28mm",   /* Caps the height for the PDF layout */
                    objectFit: "contain" 
                }} 
            />
        </div>
    </header>
);

const SectionCard = ({ title, subtitle, children, className = '', avoidBreak = false }) => (
    <section className={`pdf-section ${avoidBreak ? 'page-break-avoid' : ''} ${className}`} style={{ marginBottom: '24px' }}>
        <div className="pdf-section-title-block" style={{ backgroundColor: '#e9e5f8', borderLeft: '4px solid #d4af37', padding: '10px 15px', marginBottom: '16px', display: 'flex', flexDirection: 'column' }}>
            <h5 className="pdf-section-title m-0" style={{ color: '#2b2a35', fontWeight: '700', fontSize: '1.1rem', textTransform: 'uppercase' }}>
                {title}
            </h5>
            {subtitle && <span className="pdf-section-subtitle text-muted mt-1" style={{ fontSize: '0.85rem', fontWeight: '600' }}>{subtitle}</span>}
        </div>
        <div className="pdf-section-body" style={{ padding: '0 5px' }}>{children}</div>
    </section>
);

const FieldPair = ({ label, value, fullWidth = false }) => (
    <div className={`pdf-field-pair ${fullWidth ? 'pdf-field-pair--full' : ''}`} style={{ display: 'flex', flexDirection: fullWidth ? 'column' : 'row', padding: '6px 0', alignItems: fullWidth ? 'flex-start' : 'center' }}>
        <span className="pdf-field-label text-muted text-uppercase" style={{ width: fullWidth ? '100%' : '40%', fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.5px' }}>
            {label}
        </span>
        <span className={`pdf-field-value ${fullWidth ? 'pdf-field-value--address mt-1' : ''}`} style={{ width: fullWidth ? '100%' : '60%', fontSize: '0.9rem', fontWeight: '600', color: '#1a1a1a' }}>
            {value || '-'}
        </span>
    </div>
);

const FieldRow = ({ left, right, full = false }) => (
    <div className={`pdf-field-row ${full ? 'pdf-field-row--full' : ''}`} style={{ display: 'flex', width: '100%', gap: '20px' }}>
        <div style={{ flex: right ? '1' : '100%' }}>
            {left && <FieldPair label={left.label} value={left.value} fullWidth={left.address} />}
        </div>
        {right && (
            <div style={{ flex: '1' }}>
                <FieldPair label={right.label} value={right.value} fullWidth={right.address} />
            </div>
        )}
    </div>
);

const TimelineIcon = ({ status }) => {
    const s = (status || '').toLowerCase();
    const iconStyle = { width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: 'bold' };
    
    if (['completed', 'yes', 'received', 'approved', 'checked'].includes(s)) {
        return <div style={{ ...iconStyle, backgroundColor: '#198754' }}>✓</div>;
    }
    return <div style={{ ...iconStyle, backgroundColor: '#dc3545' }}>✕</div>;
};

const TimelineItem = ({ title, badge, badgeVariant, details = [], date }) => (
    <li className="pdf-timeline-item page-break-avoid" style={{ 
        display: 'grid', 
        gridTemplateColumns: '24px 1.5fr 1.5fr 110px', 
        alignItems: 'center', 
        gap: '12px', 
        padding: '8px 0', 
        borderBottom: '1px solid #f1f3f7' 
    }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <TimelineIcon status={badgeVariant || badge} />
        </div>
        <div className="pdf-timeline-title text-uppercase" style={{ fontWeight: '700', color: '#1a1a1a', fontSize: '12px', margin: 0 }}>
            {title}
        </div>
        <div className="pdf-timeline-details text-muted text-uppercase" style={{ fontSize: '10px', fontWeight: '600' }}>
            {details.filter(Boolean).map((line, i) => (
                <span key={i} style={{ display: 'block', lineHeight: '1.2' }}>{line}</span>
            ))}
        </div>
        <div className="pdf-timeline-date-wrapper" style={{ textAlign: 'right' }}>
            {date ? <time className="pdf-timeline-date text-muted" style={{ fontSize: '9px', fontWeight: '600', whiteSpace: 'nowrap' }}>{date}</time> : <span />}
        </div>
    </li>
);

// --- MAIN COMPONENT ---

export const RejPrintdoc = ({ setdocrview, id, onclose, showsToast }) => {
    const { documents, ecs } = useContext(Datacontext)
    const { setDim } = useContext(Dimcontext);
    const [doc_view, set_doc_view] = useState({});
    const printref = useRef(null);

    useEffect(() => {
            const viewonedoc = () => {
                const d = documents.find(item => item.id === id);
                set_doc_view(d || {});
            };
            viewonedoc()
        }, [id, documents])

    const linkedEcFull = useMemo(() => {
        if (doc_view?.already_applied_ec !== 'yes' || !doc_view?.linked_ec?.id) return null;
        return (ecs || []).find((ec) => ec.id === doc_view.linked_ec.id) || null;
    }, [doc_view, ecs]);

    const hasStampPaper = Array.isArray(doc_view?.stamp_Paper)
        && doc_view.stamp_Paper.length > 0
        && doc_view.stamp_Paper.some((s) => s?.value || s?.number);

    const presencelessLabel = String(doc_view?.status || 'offline').toLowerCase() === 'online' ? 'Online' : 'Offline';
    const alreadyAppliedEcLabel = doc_view?.already_applied_ec === 'yes' ? 'Yes' : 'No';

    const handledviewclose = () => {
        setdocrview(false);
        setDim(false);
    };

    const handlePrint = async () => {
        try {
            const element = document.getElementById("pdf-content");

            const opt = {
                margin: [15, 15, 20, 15], 
                filename: `${doc_view.application_no || 'Rejected_Document'}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: {
                    scale: 2, 
                    useCORS: true,
                    logging: false,
                    letterRendering: true,
                    windowWidth: 794,
                },
                jsPDF: {
                    unit: "mm",
                    format: "a4",
                    orientation: "portrait",
                },
                pagebreak: { mode: ['css', 'legacy'], avoid: ['.page-break-avoid', '.pdf-section'] },
            };

            html2pdf().set(opt).from(element).toPdf().get('pdf').then((pdf) => {
                const total = pdf.internal.getNumberOfPages();
                for (let i = 1; i <= total; i++) {
                    pdf.setPage(i);
                    const pageWidth = pdf.internal.pageSize.getWidth();
                    const pageHeight = pdf.internal.pageSize.getHeight();
                    
                    pdf.setTextColor(150, 150, 150);
                    pdf.setFontSize(8);
                    pdf.text(`${formatDateTime(doc_view?.updatedAt)}`, 15, pageHeight - 10, { align: "left" });
                    pdf.setFontSize(9);
                    pdf.text(`Page ${i} of ${total}`, pageWidth / 2, pageHeight - 10, { align: "center" });
                    pdf.setFontSize(8);
                    pdf.text(`${doc_view?.application_no || ''}`, pageWidth - 15, pageHeight - 13, { align: "right" });
                    pdf.setFontSize(7);
                    pdf.text("Generated by Thirupathi Documentation", pageWidth - 15, pageHeight - 9, { align: "right" });
                }
            }).save().then(() => {
                setdocrview(false);
                setDim(false);
                onclose();
            });

        } catch (error) {
            console.log(error);
        }
    };

    function DDMMYYYY(dateInput) {
        if (!dateInput) return '-';
        const date = new Date(dateInput);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    }

    const TitleCase = (str) => {
        return str ? str.charAt(0).toUpperCase() + str.slice(1) : "-";
    }

    const formatDateTime = (d) => {
        if (!d?._seconds) return '-';
        const dt = new Date(d._seconds * 1000);
        const date = dt.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const time = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
        return `${date} - ${time}`;
    };

    const getEcaYear = () => {
        const fromDoc = doc_view?.document_date?.split?.('-')?.[0];
        if (fromDoc && /^\d{4}$/.test(fromDoc)) return fromDoc;
        const fromEcDate = linkedEcFull?.date ? String(linkedEcFull.date).match(/(20\d{2})/)?.[1] : null;
        return fromEcDate || String(new Date().getFullYear());
    };

    // Match Offline PDF format: ECA/Online/{number}/{year}
    const formatEcaNo = (rawNo) => {
        if (rawNo === undefined || rawNo === null || rawNo === '') return '';
        const n = String(rawNo).trim();
        if (!n || n === '-') return '';
        if (/^ECA\/Online\//i.test(n)) return n;
        return `ECA/Online/${n}/${getEcaYear()}`;
    };

    // Upgraded Status Resolver for Rejections
    const resolveStatus = (val, yesLabel = 'Completed') => {
        if (val === 'yes') return { badge: yesLabel, variant: 'completed' };
        if (val === 'no') return { badge: 'No', variant: 'no' };
        if (val) return { badge: 'Rejected', variant: 'rejected' }; 
        return { badge: '-', variant: 'pending' };
    };

    const buildTimeline = () => {
        const items = [];
        
        items.push({
            title: 'Guidline Value Check',
            ...resolveStatus(doc_view?.value_check?.values),
            details: doc_view?.value_check?.values === 'yes' ? [`Document Value: ${doc_view?.value_check?.doc_value}`] : [],
            date: doc_view?.value_check?.values === 'yes' && formatDateTime(doc_view?.value_check?.updatedAt),
        });

        items.push({
            title: 'Draft Prepared',
            ...resolveStatus(doc_view?.draft_prepared?.values),
            date: doc_view?.draft_prepared?.values === 'yes' && formatDateTime(doc_view?.draft_prepared?.updatedAt),
        });

        items.push({
            title: 'Draft Sent',
            ...resolveStatus(doc_view?.draft_send?.values),
            details: doc_view?.draft_send?.values === 'yes' ? [`Through: ${doc_view?.draft_send?.through}`] : [],
            date: doc_view?.draft_send?.values === 'yes' && formatDateTime(doc_view?.draft_send?.updatedAt),
        });

        items.push({
            title: 'Draft Approved',
            ...resolveStatus(doc_view?.draft_approved?.values),
            details: doc_view?.draft_approved?.values === 'yes' ? [`By: ${doc_view?.draft_approved?.by}`] : [],
            date: doc_view?.draft_approved?.values === 'yes' && formatDateTime(doc_view?.certificate_applied?.updatedAt),
        });

        if (doc_view?.already_applied_ec === 'yes' && (linkedEcFull || doc_view?.linked_ec)) {
            const linked = doc_view.linked_ec || {};
            const ec = linkedEcFull || {};
            const ownerName = (ec.owner?.name || linked.owner_name || 'N/A').toUpperCase();
            const registerOffice = ec.register_office || linked.register_office || '';
            const applicationNo = ec.application_no || linked.application_no || 'N/A';
            const ecNo = ec.ecno?.no || linked.ecno?.no || '';
            const ecNoYes = ec.ecno?.values === 'yes' || ec.ecno?.value === 'yes' || linked.ecno?.values === 'yes';
            const linkedDetails = [
                `Application No.: ${applicationNo}`,
                `Executant (Owner): ${ownerName}`,
            ];
            if (registerOffice) linkedDetails.push(`Register Office: ${registerOffice}`);
            if (ec.zone) linkedDetails.push(`Zone: ${ec.zone}`);
            if (ecNoYes && ecNo) linkedDetails.push(`ECA No.: ${formatEcaNo(ecNo)}`);
            if (ec.estimated_cost) linkedDetails.push(`Estimated Cost: ${ec.estimated_cost}`);
            if (ec.docstatus || linked.docstatus) linkedDetails.push(`EC Status: ${(ec.docstatus || linked.docstatus || '').toUpperCase()}`);
            items.push({
                title: 'Already Applied EC',
                badge: 'Linked',
                variant: 'completed',
                details: linkedDetails,
                date: formatDateTime(ec.updatedAt) || formatDateTime(linked.updatedAt),
            });
        } else {
            items.push({
                title: 'Encumbrance Certificate Applied',
                ...resolveStatus(doc_view?.certificate_applied?.values),
                details: doc_view?.certificate_applied?.values === 'yes'
                    ? [`ECA No.: ${formatEcaNo(doc_view?.certificate_applied?.eca_no)}`]
                    : [],
                date: doc_view?.certificate_applied?.values === 'yes' && formatDateTime(doc_view?.certificate_applied?.updatedAt),
            });

            items.push({
                title: 'Encumbrance Certificate',
                ...resolveStatus(doc_view?.certificate_received?.values),
                date: doc_view?.certificate_received?.values === 'yes' && formatDateTime(doc_view?.certificate_received?.updatedAt),
            });
        }

        items.push({
            title: 'Temporary Deed Created',
            ...resolveStatus(doc_view?.deed_no?.values),
            details: doc_view?.deed_no?.values === 'yes'
                ? [`Tp No.: tp/${doc_view?.deed_no?.tp_no}/${doc_view?.document_date?.split?.('-')?.[0]}`]
                : [],
            date:  doc_view?.deed_no?.values === 'yes' && formatDateTime(doc_view?.deed_no?.updatedAt),
        });

        const tok = doc_view?.token_initiated;
        const tokenDetails = [];
        if (tok?.values === 'yes') {
            tokenDetails.push(`Token No.: D${tok?.token_no}`);
            if (tok?.time) tokenDetails.push(`Time: ${tok.time}`);
        }
        items.push({
            title: 'Token Initiated',
            ...resolveStatus(tok?.values),
            details: tokenDetails,
            date:  tok?.values === "yes" && formatDateTime(tok?.updatedAt),
        });

        items.push({
            title: 'Document Registered',
            ...resolveStatus(doc_view?.document_registered?.values),
            details: doc_view?.document_registered?.values === 'yes'
                ? [`Document No.: ${doc_view?.document_registered?.document_no}/${doc_view?.document_date?.split?.('-')?.[0]}`]
                : [],
            date: doc_view?.document_registered?.values === "yes" && formatDateTime(doc_view?.document_registered?.updatedAt),
        });

        items.push({
            title: 'Document Collected',
            ...resolveStatus(doc_view?.document_collected?.values),
            date: doc_view?.document_collected?.values === "yes" && formatDateTime(doc_view?.document_collected?.updatedAt),
        });

        items.push({
            title: 'Document Scanned',
            ...resolveStatus(doc_view?.document_scanned?.values),
            date: doc_view?.document_scanned?.values === "yes" && formatDateTime(doc_view?.document_scanned?.updatedAt),
        });

        items.push({
            title: 'Document Payment Completed',
            ...resolveStatus(doc_view?.payment?.values),
            date: doc_view?.payment?.values === "yes" && formatDateTime(doc_view?.payment?.updatedAt),
        });

        items.push({
            title: 'Collected By Above Clients, Ask Buyer',
            ...resolveStatus(doc_view?.other),
            date: '', // Date not provided in original for this field
        });

        return items;
    };

    const subHeaderStyle = { backgroundColor: '#e9e5f8', borderLeft: '4px solid #d4af37', padding: '8px 15px', display: 'flex', flexDirection: 'column' };
    const subTitleStyle = { color: '#2b2a35', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', margin: 0 };
    const subTitleMutedStyle = { fontSize: '0.85rem', fontWeight: '600', color: '#47494b', marginTop: '2px' };

    return (
        <div className="viewd p-4" style={{ backgroundColor: '#f0f2f5', borderRadius: '12px' }}>
            <div className='d-flex justify-content-between align-items-center mb-3 px-3'>
                <h4 className="m-0 text-danger fw-bold" style={{ letterSpacing: '0.5px' }}>Rejected Document</h4>
                <Button variant="outline-danger" size="sm" onClick={handledviewclose} className="rounded-circle p-2" style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <MdClose size={20} />
                </Button>
            </div>
            
            <SimpleBar style={{ width: '100%', height: '70vh' }} className="bg-white rounded-3 shadow-sm border p-4">
                
                <div id="pdf-content" ref={printref} className="pdf-document" style={{ fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif', color: '#333' }}>

                    <PdfBrandHeader />

                    {/* --- REJECTION REASON ALERT BLOCK --- */}
                    {doc_view?.rejreason && (
                        <div className="pdf-rejection-block page-break-avoid" style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c2c7', borderLeft: '4px solid #dc3545', padding: '15px', marginBottom: '24px', borderRadius: '4px' }}>
                            <h5 style={{ color: '#dc3545', fontWeight: '700', fontSize: '1rem', marginTop: 0, marginBottom: '8px', textTransform: 'uppercase' }}>Reason for Rejection</h5>
                            <p style={{ color: '#842029', margin: 0, fontWeight: '600', fontSize: '0.95rem' }}>{doc_view.rejreason}</p>
                        </div>
                    )}

                    <SectionCard title="Application Details">
                        <FieldRow
                            left={{ label: 'Application No', value: doc_view?.application_no }}
                            right={{ label: 'Date', value: doc_view?.date }}
                        />
                        <FieldRow
                            left={{ label: 'Register Office', value: TitleCase(doc_view?.register_office) }}
                            right={{ label: 'Nature of Deed', value: TitleCase(doc_view?.register_document) }}
                        />
                        <FieldRow
                            left={{ label: 'Referred By', value: TitleCase(doc_view?.refered_by?.name) }}
                            right={{ label: 'Document Date', value: DDMMYYYY(doc_view?.document_date) }}
                        />
                        <FieldRow
                            left={{
                                label: 'Presenceless',
                                value: <StatusBadge label={presencelessLabel} variant={presencelessLabel === 'Online' ? 'yes' : 'checked'} />,
                            }}
                            right={{
                                label: 'Already Applied EC',
                                value: <StatusBadge label={alreadyAppliedEcLabel} variant={alreadyAppliedEcLabel === 'Yes' ? 'yes' : 'no'} />,
                            }}
                        />
                    </SectionCard>

                    {/* --- BUYERS SECTION --- */}
                    {doc_view?.buyers?.length > 0 && (
                        <div className="pdf-flat-section" style={{ display: 'block', width: '100%' }}>
                            <div className="pdf-nested-member-block page-break-avoid" style={{ marginBottom: '20px' }}>
                                <div className="pdf-section-title-block rounded-top" style={subHeaderStyle}>
                                    <h3 className="pdf-section-title" style={subTitleStyle}>Applicant Details</h3>
                                    <span className="pdf-section-subtitle" style={subTitleMutedStyle}>
                                        {doc_view.buyers.length === 1 ? 'Buyer Detail' : 'Buyers Details'}
                                    </span>
                                </div>
                                <div className="border border-top-0 rounded-bottom p-3">
                                    <FieldRow left={{ label: '1. Name', value: doc_view.buyers[0]?.buyer_Detail?.name }} right={{ label: 'Phone', value: doc_view.buyers[0]?.buyer_Detail?.mobile }} />
                                    <FieldRow full left={{ label: 'Address', value: `${doc_view.buyers[0]?.buyer_Detail?.address || ''}, ${doc_view.buyers[0]?.buyer_Detail?.city || ''}, ${doc_view.buyers[0]?.buyer_Detail?.state || ''} - ${doc_view.buyers[0]?.buyer_Detail?.pincode || ''}`.replace(/, ,/g, ','), address: true }} />
                                    <FieldRow left={{ label: 'Aadhar Card', value: doc_view.buyers[0]?.buyer_aadhar }} right={null} />
                                </div>
                            </div>

                            {doc_view.buyers.length > 1 && doc_view.buyers.slice(1).map((buyer, index) => (
                                <div key={buyer?.id || `buyer-${index}`} className="pdf-nested-member-block page-break-avoid border rounded p-3" style={{ marginBottom: '20px' }}>
                                    <FieldRow left={{ label: `${index + 2}. Name`, value: buyer?.buyer_Detail?.name }} right={{ label: 'Phone', value: buyer?.buyer_Detail?.mobile }} />
                                    <FieldRow full left={{ label: 'Address', value: `${buyer?.buyer_Detail?.address || ''}, ${buyer?.buyer_Detail?.city || ''}, ${buyer?.buyer_Detail?.state || ''} - ${buyer?.buyer_Detail?.pincode || ''}`.replace(/, ,/g, ','), address: true }} />
                                    <FieldRow left={{ label: 'Aadhar Card', value: buyer?.buyer_aadhar }} right={null} />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* --- SELLERS SECTION --- */}
                    {doc_view?.sellers?.length > 0 && (
                        <div className="pdf-flat-section" style={{ display: 'block', width: '100%' }}>
                            <div className="pdf-nested-member-block page-break-avoid" style={{ marginBottom: '20px' }}>
                                <div className="pdf-section-title-block rounded-top" style={subHeaderStyle}>
                                    <span className="pdf-section-subtitle" style={subTitleMutedStyle}>
                                        {doc_view.sellers.length === 1 ? 'Seller Detail' : 'Sellers Details'}
                                    </span>
                                </div>
                                <div className="border border-top-0 rounded-bottom p-3">
                                    <FieldRow left={{ label: '1. Name', value: doc_view.sellers[0]?.seller_Detail?.name }} right={{ label: 'Phone', value: doc_view.sellers[0]?.seller_Detail?.mobile }} />
                                    <FieldRow full left={{ label: 'Address', value: `${doc_view.sellers[0]?.seller_Detail?.address || ''}, ${doc_view.sellers[0]?.seller_Detail?.city || ''}, ${doc_view.sellers[0]?.seller_Detail?.state || ''} - ${doc_view.sellers[0]?.seller_Detail?.pincode || ''}`.replace(/, ,/g, ','), address: true }} />
                                    <FieldRow left={{ label: 'Aadhar Card', value: doc_view.sellers[0]?.seller_aadhar }} right={null} />
                                </div>
                            </div>

                            {doc_view.sellers.length > 1 && doc_view.sellers.slice(1).map((seller, index) => (
                                <div key={seller?.id || `seller-${index}`} className="pdf-nested-member-block page-break-avoid border rounded p-3" style={{ marginBottom: '20px' }}>
                                    <FieldRow left={{ label: `${index + 2}. Name`, value: seller?.seller_Detail?.name }} right={{ label: 'Phone ', value: seller?.seller_Detail?.mobile }} />
                                    <FieldRow full left={{ label: 'Address', value: `${seller?.seller_Detail?.address || ''}, ${seller?.seller_Detail?.city || ''}, ${seller?.seller_Detail?.state || ''} - ${seller?.seller_Detail?.pincode || ''}`.replace(/, ,/g, ','), address: true }} />
                                    <FieldRow left={{ label: 'Aadhar Card', value: seller?.seller_aadhar }} right={null} />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* --- WITNESS SECTION --- */}
                    {doc_view?.witness?.length > 0 && (
                        <div className="pdf-flat-section" style={{ display: 'block', width: '100%' }}>
                            <div className="pdf-nested-member-block page-break-avoid" style={{ marginBottom: '20px' }}>
                                <div className="pdf-section-title-block rounded-top" style={subHeaderStyle}>
                                    <span className="pdf-section-subtitle" style={subTitleMutedStyle}>
                                        {doc_view.witness.length === 1 ? 'Witness Detail' : 'Witness Details'}
                                    </span>
                                </div>
                                <div className="border border-top-0 rounded-bottom p-3">
                                    <FieldRow left={{ label: '1. Name', value: doc_view.witness[0]?.witness_Detail?.name }} right={{ label: 'Phone', value: doc_view.witness[0]?.witness_Detail?.mobile }} />
                                    <FieldRow full left={{ label: 'Address', value: `${doc_view.witness[0]?.witness_Detail?.address || ''}, ${doc_view.witness[0]?.witness_Detail?.city || ''}, ${doc_view.witness[0]?.witness_Detail?.state || ''} - ${doc_view.witness[0]?.witness_Detail?.pincode || ''}`.replace(/, ,/g, ','), address: true }} />
                                    <FieldRow left={{ label: 'Aadhar Card', value: doc_view.witness[0]?.witness_aadhar }} right={null} />
                                </div>
                            </div>

                            {doc_view.witness.length > 1 && doc_view.witness.slice(1).map((wit, index) => (
                                <div key={wit?.id || `witness-${index}`} className="pdf-nested-member-block page-break-avoid border rounded p-3" style={{ marginBottom: '20px' }}>
                                    <FieldRow left={{ label: `${index + 2}. Name`, value: wit?.witness_Detail?.name }} right={{ label: 'Phone', value: wit?.witness_Detail?.mobile }} />
                                    <FieldRow full left={{ label: 'Address', value: `${wit?.witness_Detail?.address || ''}, ${wit?.witness_Detail?.city || ''}, ${wit?.witness_Detail?.state || ''} - ${wit?.witness_Detail?.pincode || ''}`.replace(/, ,/g, ','), address: true }} />
                                    <FieldRow left={{ label: 'Aadhar Card', value: wit?.witness_aadhar }} right={null} />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* CONSOLIDATED DOCUMENT DETAILS */}
                    <div className="pdf-flat-section" style={{ display: 'block', width: '100%' }}>
                                                
                                                {/* BRICK 1: Section Title & Property Info (Unbreakable) */}
                                                <div className="pdf-nested-member-block page-break-avoid" style={{ marginBottom: '24px' }}>
                                                    <div className="pdf-section-title-block" style={{ backgroundColor: '#e9e5f8', borderLeft: '4px solid #d4af37', padding: '10px 15px', marginBottom: '16px' }}>
                                                        <h2 className="pdf-section-title m-0" style={{ color: '#2b2a35', fontWeight: '700', fontSize: '1.1rem', textTransform: 'uppercase' }}>Property & Document Details</h2>
                                                    </div>
                                                    <div className="px-2">
                                                        <FieldRow left={{ label: 'Village Name', value: doc_view?.village }} right={{ label: 'S.F No.', value: doc_view?.sfno }} />
                                                        <FieldRow left={{ label: 'Site No.', value: doc_view?.site_no }} right={{ label: 'Layout Name', value: doc_view?.layout_name }} />
                                                        <FieldRow
                                                            left={{
                                                                label: 'Document Need to Split',
                                                                value: doc_view?.document_split === 'yes' ? <StatusBadge label="YES" variant="yes" /> : <StatusBadge label="No" variant="no" />,
                                                            }}
                                                            right={{ label: 'Approximate Quotation', value: doc_view?.approximate_quatation }}
                                                        />
                                                        <FieldRow
                                                            left={{
                                                                label: 'Stamp Paper',
                                                                value: hasStampPaper
                                                                    ? <StatusBadge label="Yes" variant="yes" />
                                                                    : <StatusBadge label="No" variant="no" />,
                                                            }}
                                                            right={null}
                                                        />
                                                    </div>
                                                </div>
                    
                                                {/* BRICK 2: Parent Document Table (Unbreakable) */}
                                                {typeof doc_view?.parent_document === 'object' && doc_view?.parent_document?.length > 0 && (
                                                    <div className="pdf-nested-member-block page-break-avoid" style={{ marginBottom: '24px' }}>
                                                        <Table bordered hover striped size="sm" className="mb-0 border-light" style={{ borderColor: '#e9ecef' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th colSpan={3} className="bg-light text-muted text-uppercase" style={{ fontSize: '0.85rem', padding: '10px', borderBottom: '2px solid #dee2e6' }}>Parent Document Details</th>
                                                                </tr>
                                                                <tr className="bg-white text-dark" style={{ fontSize: '0.85rem' }}>
                                                                    <th style={{ width: '10%', padding: '8px' }}>No.</th>
                                                                    <th style={{ width: '45%', padding: '8px' }}>Type</th>
                                                                    <th style={{ width: '45%', padding: '8px' }}>Document Number</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {doc_view.parent_document.map((doc, index) => (
                                                                    <tr key={index}>
                                                                        <td className="fw-bold" style={{ padding: '8px' }}>{index + 1}.</td>
                                                                        <td className="text-dark fw-medium" style={{ padding: '8px' }}>{doc?.type || '-'}</td>
                                                                        <td className="text-dark fw-medium" style={{ padding: '8px' }}>{doc?.number || '-'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                )}
                    
                                                {/* BRICK 3: Stamp Paper Details Table (Unbreakable) — only when Yes */}
                                                {hasStampPaper && (
                                                    <div className="pdf-nested-member-block page-break-avoid" style={{ marginBottom: '24px' }}>
                                                        <Table bordered hover striped size="sm" className="mb-0 border-light" style={{ borderColor: '#e9ecef' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th colSpan={3} className="bg-light text-muted text-uppercase" style={{ fontSize: '0.85rem', padding: '10px', borderBottom: '2px solid #dee2e6' }}>Stamp Paper Details</th>
                                                                </tr>
                                                                <tr className="bg-white text-dark" style={{ fontSize: '0.85rem' }}>
                                                                    <th style={{ width: '10%', padding: '8px' }}>No.</th>
                                                                    <th style={{ width: '45%', padding: '8px' }}>Stamp Value</th>
                                                                    <th style={{ width: '45%', padding: '8px' }}>Stamp Number</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {doc_view.stamp_Paper.map((stamp, index) => (
                                                                    <tr key={index}>
                                                                        <td className="fw-bold" style={{ padding: '8px' }}>{index + 1}.</td>
                                                                        <td className="text-dark fw-medium" style={{ padding: '8px' }}>{stamp?.value || '-'}</td>
                                                                        <td className="text-dark fw-medium" style={{ padding: '8px' }}>{stamp?.number || '-'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                )}

                                                {/* BRICK 4: Already Applied EC Details from registered_Ec */}
                                                {doc_view?.already_applied_ec === 'yes' && (
                                                    <div className="pdf-nested-member-block page-break-avoid" style={{ marginBottom: '24px' }}>
                                                        <div className="pdf-section-title-block" style={{ backgroundColor: '#e9e5f8', borderLeft: '4px solid #d4af37', padding: '10px 15px', marginBottom: '16px' }}>
                                                            <h2 className="pdf-section-title m-0" style={{ color: '#2b2a35', fontWeight: '700', fontSize: '1.1rem', textTransform: 'uppercase' }}>Already Applied EC Details</h2>
                                                        </div>
                                                        <div className="px-2">
                                                            <FieldRow
                                                                left={{ label: 'EC Application No', value: (linkedEcFull?.application_no || doc_view?.linked_ec?.application_no || '-').toUpperCase() }}
                                                                right={{ label: 'EC Date', value: linkedEcFull?.date || '-' }}
                                                            />
                                                            <FieldRow
                                                                left={{ label: 'Owner / Executant', value: (linkedEcFull?.owner?.name || doc_view?.linked_ec?.owner_name || '-').toUpperCase() }}
                                                                right={{ label: 'Owner Mobile', value: linkedEcFull?.owner?.mobile || '-' }}
                                                            />
                                                            <FieldRow
                                                                left={{ label: 'Zone', value: TitleCase(linkedEcFull?.zone) }}
                                                                right={{ label: 'Register Office', value: TitleCase(linkedEcFull?.register_office || doc_view?.linked_ec?.register_office) }}
                                                            />
                                                            <FieldRow
                                                                left={{
                                                                    label: 'EC Mode',
                                                                    value: TitleCase(
                                                                        typeof linkedEcFull?.status === 'string'
                                                                            ? linkedEcFull.status
                                                                            : (linkedEcFull?.status?.status || '-')
                                                                    ),
                                                                }}
                                                                right={{
                                                                    label: 'ECA No.',
                                                                    value: (linkedEcFull?.ecno?.values === 'yes' || linkedEcFull?.ecno?.value === 'yes' || doc_view?.linked_ec?.ecno?.values === 'yes')
                                                                        ? (formatEcaNo(linkedEcFull?.ecno?.no || doc_view?.linked_ec?.ecno?.no) || '-')
                                                                        : '-',
                                                                }}
                                                            />
                                                            <FieldRow
                                                                left={{ label: 'Estimated Cost', value: linkedEcFull?.estimated_cost || '-' }}
                                                                right={{ label: 'EC Doc Status', value: TitleCase(linkedEcFull?.docstatus || doc_view?.linked_ec?.docstatus) }}
                                                            />
                                                            <FieldRow
                                                                left={{
                                                                    label: 'Managed By',
                                                                    value: Array.isArray(linkedEcFull?.managed_By) && linkedEcFull.managed_By.length > 0
                                                                        ? linkedEcFull.managed_By.map((n) => String(n).toUpperCase()).join(', ')
                                                                        : '-',
                                                                }}
                                                                right={{
                                                                    label: 'Referred By',
                                                                    value: TitleCase(linkedEcFull?.refered_by?.name),
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                    {/* STATUS TRACKER */}
                    <div className="pdf-flat-section" style={{ display: 'block', width: '100%' }}>
                        <SectionCard title="Process / Status Tracker">
                            <ul className="pdf-timeline m-0 p-0" style={{ listStyleType: 'none' }}>
                                {buildTimeline().map((item, index) => (
                                    <TimelineItem key={index} {...item} />
                                ))}
                            </ul>
                        </SectionCard>
                    </div>
                    {doc_view?.remarks && (
    <div className="pdf-flat-section mt-3" style={{ display: 'block', width: '100%' }}>
        <SectionCard title="Remarks">
            <div 
                className="p-3 rounded border" 
                style={{ 
                    fontSize: '12px', 
                    color: '#495057', 
                    backgroundColor: '#f8f9fa',
                    whiteSpace: 'pre-wrap', // Preserves the 5-6 line breaks if typed
                    lineHeight: '1.5'
                }}
            >
                {doc_view.remarks}
            </div>
        </SectionCard>
    </div>
)}
<div className="unbreakable-closing-block" style={{ pageBreakInside: 'avoid', breakInside: 'avoid', marginTop: '30px' }}>
                            
                            {/* SECTION: Declaration */}
                            <SectionCard title="Declaration of Receipt and Responsibility of Documents">
    <div className="pdf-declaration-card bg-light border p-4 rounded-3" style={{ borderColor: '#e9ecef', display: 'block', width: '100%' }}>
    
    <div className="pdf-declaration-block" style={{ marginBottom: '20px', display: 'block' }}>
        <p style={{ 
            fontWeight: 700, 
            color: '#1b2559', 
            textTransform: 'uppercase', 
            marginBottom: '12px', 
            lineHeight: 1.6, 
            fontSize: '0.95rem', 
            textAlign: 'justify', 
            display: 'block' 
        }}>
            I HEREBY SOLEMNLY DECLARE THAT I HAVE RECEIVED ALL THE DOCUMENTS FROM YOUR OFFICE IN FULL AND IN GOOD CONDITION.
        </p>
        <p style={{ 
            color: '#424e79', 
            fontStyle: 'italic', 
            marginBottom: 0, 
            lineHeight: 1.5, 
            fontSize: '0.9rem', 
            textAlign: 'justify', 
            display: 'block' 
        }}>
            (உங்கள் அலுவலகத்திலிருந்து அனைத்து ஆவணங்களையும் முழுமையாகவும், சரியான நிலையிலும் பெற்றுக்கொண்டேன் என்று இதில் உறுதியாக அறிவிக்கிறேன்.)
        </p>
    </div>
    
    <div className="pdf-declaration-block" style={{ display: 'block' }}>
        <p style={{ 
            fontWeight: 700, 
            color: '#1b2559', 
            textTransform: 'uppercase', 
            marginBottom: '12px', 
            lineHeight: 1.6, 
            fontSize: '0.95rem', 
            textAlign: 'justify', 
            display: 'block' 
        }}>
            I FURTHER DECLARE THAT I HAVE CAREFULLY VERIFIED ALL PAGES CONTAINED IN THE SAID DOCUMENTS AT THE TIME OF RECEIPT. I ACCEPT COMPLETE RESPONSIBILITY FOR THE CUSTODY AND SAFETY OF THESE DOCUMENTS HENCEFORTH. IN THE EVENT OF ANY LOSS, MISSING PAGES, OR DAMAGE (INCLUDING TEARING) TO THE DOCUMENTS AFTER RECEIPT, I SHALL BE SOLELY RESPONSIBLE FOR THE SAME.
        </p>
        <p style={{ 
            color: '#424e79', 
            fontStyle: 'italic', 
            marginBottom: 0, 
            lineHeight: 1.5, 
            fontSize: '0.9rem', 
            textAlign: 'justify', 
            display: 'block' 
        }}>
            (பெறப்பட்ட ஆவணங்களில் உள்ள அனைத்து பக்கங்களையும் பெற்றுக்கொள்ளும் நேரத்தில் கவனமாகச் சரிபார்த்துள்ளேன். இவ்வாவணங்களின் பாதுகாப்பு மற்றும் பராமரிப்பிற்கான முழுப் பொறுப்பையும் இனிமேல் நான் ஏற்கிறேன். பெற்றுக்கொண்ட பின்பு, ஆவணங்களில் ஏதேனும் இழப்பு, பக்கங்கள் காணாமல் போதல் அல்லது கிழிவு உள்ளிட்ட சேதங்கள் ஏற்பட்டால், அதற்கான முழுப் பொறுப்பு எனதே.)
        </p>
    </div>
    
</div>
</SectionCard>

                            {/* SECTION: Signatory */}
                            <SectionCard title="Document Receiver Details">
                                <div className="px-2">
                                    {doc_view?.other?.values === 'no' ? (
                                        <>
                                            <FieldRow left={{ label: 'Person Name', value: '  ' }} right={null} />
                                            <FieldRow left={{ label: 'Mobile No.', value: '  ' }} right={null} />
                                            <FieldRow full left={{ label: 'Address', value: '  ', address: true }} />
                                        </>
                                    ) : (
                                        <>
                                            <FieldRow left={{ label: 'Person Name', value: doc_view.other?.cusDetail?.name }} right={null} />
                                            <FieldRow left={{ label: 'Mobile No.', value: doc_view.other?.cusDetail?.mobile }} right={null} />
                                            <FieldRow
                                                full
                                                left={{
                                                    label: 'Address',
                                                    value: doc_view.other?.cusDetail ? (
                                                        <span>{`${doc_view.other.cusDetail.address || ''}, ${doc_view.other.cusDetail.city || ''}, ${doc_view.other.cusDetail.state || ''}-${doc_view.other.cusDetail.pincode || ''}`.replace(/, ,/g, ',')}</span>
                                                    ) : '',
                                                    address: true,
                                                }}
                                            />
                                        </>
                                    )}
                                </div>
                                <div className="pdf-signature-row mt-5 d-flex justify-content-between px-3">
                                    <div className="text-center" style={{ width: '250px' }}>
                                        <div className="pdf-signature-line mb-2" style={{ borderBottom: '2px solid #333', height: '40px' }} />
                                        <span className="pdf-field-label text-muted fw-bold text-uppercase" style={{ fontSize: '0.85rem' }}>Signature</span>
                                    </div>
                                    <div className="text-center" style={{ width: '250px' }}>
                                        <div className="pdf-signature-line mb-2" style={{ borderBottom: '2px solid #333', height: '40px' }} />
                                        <span className="pdf-field-label text-muted fw-bold text-uppercase" style={{ fontSize: '0.85rem' }}>Date</span>
                                    </div>
                                </div>
                            </SectionCard>
                        </div>
                    </div>

                <div className='d-flex justify-content-center mt-5 mb-4 border-top pt-4'>
                    <Button variant="danger" size="lg" className="px-5 py-2 shadow-sm fw-bold d-flex align-items-center" style={{ borderRadius: '30px' }} onClick={handlePrint}>
                        <IoMdPrint size={22} className="me-2" /> GENERATE PDF 
                    </Button>
                </div>
            </SimpleBar>
        </div>
    )
}